const VERSION = 1;
const STATUSES = new Set(["requesting", "unknown", "confirmed", "rejected"]);
const BLOCKING_STATUSES = new Set(["requesting", "unknown"]);
const TOP_LEVEL_KEYS = ["createdAt", "namespace", "requestId", "scope", "status", "updatedAt", "version"];
const FORBIDDEN_SCOPE_KEY = /(captcha|credential|password|payload|secret|token|webhook)/i;

export function operationOutcomeScope(namespace, scope) {
  const normalizedNamespace = normalizeNamespace(namespace);
  if (!isPlainObject(scope)) throw outcomeError("OPERATION_OUTCOME_SCOPE_INVALID");
  const keys = Object.keys(scope).sort();
  if (keys.length === 0 || keys.length > 16) throw outcomeError("OPERATION_OUTCOME_SCOPE_INVALID");

  const normalized = {};
  for (const key of keys) {
    if (!/^[a-z][a-zA-Z0-9]{0,63}$/.test(key) || FORBIDDEN_SCOPE_KEY.test(key)) {
      throw outcomeError("OPERATION_OUTCOME_SCOPE_INVALID");
    }
    normalized[key] = normalizeScopeValue(scope[key]);
  }
  return Object.freeze({ namespace: normalizedNamespace, scope: Object.freeze(normalized) });
}

export function operationOutcomeStorageKey({ namespace, scope }) {
  const normalized = operationOutcomeScope(namespace, scope);
  return `nitro:operation-outcome:v${VERSION}:${normalized.namespace}:${encodeURIComponent(stableScope(normalized.scope))}`;
}

export function operationOutcomeLockName({ namespace, scope }) {
  const normalized = operationOutcomeScope(namespace, scope);
  return `nitro:lock:operation-outcome:v${VERSION}:${normalized.namespace}:${encodeURIComponent(stableScope(normalized.scope))}`;
}

export function restoreOperationOutcome({ namespace, scope, storage }) {
  const normalized = operationOutcomeScope(namespace, scope);
  return readOutcome(storage, normalized);
}

export function isOperationOutcomeBlocking(outcome) {
  return BLOCKING_STATUSES.has(outcome?.status);
}

export function subscribeOperationOutcome({ namespace, scope, eventTarget, onChange }) {
  const normalized = operationOutcomeScope(namespace, scope);
  if (!eventTarget?.addEventListener || !eventTarget?.removeEventListener || typeof onChange !== "function") {
    throw outcomeError("OPERATION_OUTCOME_EVENTS_REQUIRED");
  }
  const key = operationOutcomeStorageKey(normalized);
  const listener = (event) => {
    if (event?.key === key) onChange();
  };
  eventTarget.addEventListener("storage", listener);
  return () => eventTarget.removeEventListener("storage", listener);
}

export async function runOperationOutcome({
  namespace,
  scope,
  storage,
  lockManager,
  execute,
  validateResponse = null,
  isDefinitiveRejection = defaultDefinitiveRejection,
  uuid = defaultUuid,
  now = defaultNow,
}) {
  const normalized = operationOutcomeScope(namespace, scope);
  if (typeof execute !== "function") throw outcomeError("OPERATION_OUTCOME_EXECUTE_REQUIRED");
  if (validateResponse !== null && typeof validateResponse !== "function") {
    throw outcomeError("OPERATION_OUTCOME_RESPONSE_VALIDATOR_INVALID");
  }
  if (typeof isDefinitiveRejection !== "function") {
    throw outcomeError("OPERATION_OUTCOME_REJECTION_CLASSIFIER_INVALID");
  }

  return withOutcomeLock(lockManager, normalized, async () => {
    const current = readOutcome(storage, normalized);
    if (isOperationOutcomeBlocking(current)) throw outcomeError("OPERATION_OUTCOME_PENDING");
    const requesting = beginOutcome({ normalized, storage, uuid, now });
    let response;
    try {
      response = await execute();
      if (validateResponse) await validateResponse(response);
    } catch (error) {
      if (isDefinitiveRejection(error) === true) {
        finishOutcome({ normalized, storage, current: requesting, status: "rejected", now });
        throw error;
      }
      try {
        finishOutcome({ normalized, storage, current: requesting, status: "unknown", now });
      } catch (storageError) {
        if (storageError && storageError.cause === undefined) storageError.cause = error;
        throw storageError;
      }
      throw unknownResult(error);
    }
    finishOutcome({ normalized, storage, current: requesting, status: "confirmed", now });
    return response;
  });
}

export async function reconcileOperationOutcome({
  namespace,
  scope,
  storage,
  lockManager,
  reconcile,
  now = defaultNow,
}) {
  const normalized = operationOutcomeScope(namespace, scope);
  if (typeof reconcile !== "function") throw outcomeError("OPERATION_OUTCOME_RECONCILE_REQUIRED");
  return withOutcomeLock(lockManager, normalized, async () => {
    const current = readOutcome(storage, normalized);
    if (!isOperationOutcomeBlocking(current)) return current;
    let confirmed;
    try {
      confirmed = await reconcile(current);
    } catch (error) {
      throw unknownResult(error);
    }
    if (confirmed !== true) return current;
    return finishOutcome({ normalized, storage, current, status: "confirmed", now });
  });
}

function beginOutcome({ normalized, storage, uuid, now }) {
  const at = validTimestamp(now());
  const outcome = {
    version: VERSION,
    namespace: normalized.namespace,
    scope: normalized.scope,
    requestId: validUuid(uuid()),
    status: "requesting",
    createdAt: at,
    updatedAt: at,
  };
  return persistOutcome(storage, normalized, outcome);
}

function finishOutcome({ normalized, storage, current, status, now }) {
  if (!STATUSES.has(status) || status === "requesting") throw outcomeError("OPERATION_OUTCOME_STATUS_INVALID");
  const stored = readOutcome(storage, normalized);
  if (!stored || stored.requestId !== current.requestId || stored.status !== current.status || !isOperationOutcomeBlocking(stored)) {
    throw outcomeError("OPERATION_OUTCOME_SUPERSEDED");
  }
  const next = { ...stored, status, updatedAt: validTimestamp(now()) };
  try {
    return persistOutcome(storage, normalized, next);
  } catch (error) {
    persistUnknownBestEffort(storage, normalized, stored, now);
    throw error;
  }
}

function readOutcome(storage, normalized) {
  requireStorage(storage);
  let raw;
  try {
    raw = storage.getItem(operationOutcomeStorageKey(normalized));
  } catch {
    throw outcomeError("OPERATION_OUTCOME_STORAGE_UNAVAILABLE");
  }
  if (raw === null) return null;
  try {
    const outcome = JSON.parse(raw);
    if (!validOutcome(outcome, normalized)) throw new Error("invalid");
    return freezeOutcome(outcome);
  } catch {
    throw outcomeError("OPERATION_OUTCOME_STORAGE_INVALID");
  }
}

function persistOutcome(storage, normalized, outcome) {
  requireStorage(storage);
  if (!validOutcome(outcome, normalized)) throw outcomeError("OPERATION_OUTCOME_RECORD_INVALID");
  const frozen = freezeOutcome(outcome);
  const serialized = JSON.stringify(frozen);
  const key = operationOutcomeStorageKey(normalized);
  try {
    storage.setItem(key, serialized);
    if (storage.getItem(key) !== serialized) throw new Error("unconfirmed");
  } catch {
    throw outcomeError("OPERATION_OUTCOME_STORAGE_UNCONFIRMED");
  }
  return frozen;
}

function persistUnknownBestEffort(storage, normalized, current, now) {
  try {
    const unknown = freezeOutcome({ ...current, status: "unknown", updatedAt: validTimestamp(now()) });
    storage?.setItem?.(operationOutcomeStorageKey(normalized), JSON.stringify(unknown));
  } catch {}
}

async function withOutcomeLock(lockManager, normalized, callback) {
  if (!lockManager || typeof lockManager.request !== "function") throw outcomeError("OPERATION_OUTCOME_LOCK_REQUIRED");
  let result;
  try {
    result = await lockManager.request(operationOutcomeLockName(normalized), { mode: "exclusive", ifAvailable: true }, async (lock) => {
      if (!lock) return { type: "denied" };
      try {
        return { type: "fulfilled", value: await callback() };
      } catch (error) {
        return { type: "rejected", error };
      }
    });
  } catch {
    throw outcomeError("OPERATION_OUTCOME_LOCK_FAILED");
  }
  if (result?.type === "denied") throw outcomeError("OPERATION_OUTCOME_LOCK_DENIED");
  if (result?.type === "rejected") throw result.error;
  if (result?.type === "fulfilled") return result.value;
  throw outcomeError("OPERATION_OUTCOME_LOCK_FAILED");
}

function validOutcome(outcome, normalized) {
  if (!isPlainObject(outcome) || !sameKeys(outcome, TOP_LEVEL_KEYS)) return false;
  if (outcome.version !== VERSION || outcome.namespace !== normalized.namespace) return false;
  if (!isPlainObject(outcome.scope) || stableScope(outcome.scope) !== stableScope(normalized.scope)) return false;
  if (!validUuidOrFalse(outcome.requestId) || !STATUSES.has(outcome.status)) return false;
  return validTimestampOrFalse(outcome.createdAt) && validTimestampOrFalse(outcome.updatedAt);
}

function freezeOutcome(outcome) {
  return Object.freeze({ ...outcome, scope: Object.freeze({ ...outcome.scope }) });
}

function stableScope(scope) {
  return JSON.stringify(Object.keys(scope).sort().map((key) => [key, scope[key]]));
}

function normalizeNamespace(value) {
  const normalized = String(value || "");
  if (!/^[a-z][a-z0-9-]{0,63}$/.test(normalized)) throw outcomeError("OPERATION_OUTCOME_NAMESPACE_INVALID");
  return normalized;
}

function normalizeScopeValue(value) {
  if (typeof value === "boolean") return value;
  if (typeof value === "number" && Number.isSafeInteger(value) && value >= 0) return value;
  if (typeof value === "string" && value.length > 0 && value.length <= 256 && value === value.trim() && !/[\u0000-\u001f\u007f]/.test(value)) {
    return value;
  }
  throw outcomeError("OPERATION_OUTCOME_SCOPE_INVALID");
}

function requireStorage(storage) {
  if (!storage || typeof storage.getItem !== "function" || typeof storage.setItem !== "function") {
    throw outcomeError("OPERATION_OUTCOME_STORAGE_REQUIRED");
  }
}

function sameKeys(object, expected) {
  const actual = Object.keys(object).sort();
  return actual.length === expected.length && actual.every((key, index) => key === expected[index]);
}

function validUuid(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (!validUuidOrFalse(normalized)) throw outcomeError("OPERATION_OUTCOME_REQUEST_ID_INVALID");
  return normalized;
}

function validUuidOrFalse(value) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/.test(String(value || ""));
}

function validTimestamp(value) {
  if (!validTimestampOrFalse(value)) throw outcomeError("OPERATION_OUTCOME_TIMESTAMP_INVALID");
  return value;
}

function validTimestampOrFalse(value) {
  return typeof value === "string" && value.length >= 20 && Number.isFinite(Date.parse(value));
}

function defaultDefinitiveRejection(error) {
  const status = Number(error?.status);
  return status >= 400 && status < 500;
}

function defaultUuid() {
  return globalThis.crypto?.randomUUID?.();
}

function defaultNow() {
  return new Date().toISOString();
}

function unknownResult(cause) {
  const error = outcomeError("OPERATION_OUTCOME_RESULT_UNKNOWN");
  if (cause !== undefined) error.cause = cause;
  return error;
}

function outcomeError(code) {
  const error = new Error(code);
  error.code = code;
  return error;
}

function isPlainObject(value) {
  if (value === null || typeof value !== "object" || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}
